/*
 * LETTERHEAD.JSX - Global letterhead and branding settings management page
 *
 * SUMMARY:
 * Manages company detail fields, working-day settings, and branding assets used by
 * the global settings experience.
 */
import React, { Component } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import {
  getGlobalSetting,
  editGlobalSetting,
  deleteGlobalSetting,
  getGlobalSettingReducer
} from "../../actions/global-setting.action";
import { errorSnackBar } from "../../actions/common.action";
import DeleteDialog from "../../components/delete-dialog";
import "./scss/templates.scss";
import idx from "idx";
import CustomInput, { NumberInput } from "../../components/inputs/new-input";
import Button from 'react-bootstrap/Button';
import DeleteIcon from '@mui/icons-material/Delete';
import moment from "moment";
import TimePickerInput from "../../components/inputs/time-picker";
import Form from 'react-bootstrap/Form';
import _ from "lodash";
import { ValidateField, ValidatorTypes } from "../../util/validators";
import { GSTORVAT } from 'src/util/enums';

const LETTERHEAD_PREVIEW_FRAME_STYLE = {
  width: "160px",
  height: "96px",
  border: "1px solid #dee2e6",
  borderRadius: "4px",
  backgroundColor: "#f8f9fa",
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const LETTERHEAD_PREVIEW_IMAGE_STYLE = {
  width: "100%",
  height: "100%",
  objectFit: "contain",
};

const LETTERHEAD_PREVIEW_PLACEHOLDER_STYLE = {
  padding: "12px",
  color: "#6c757d",
  fontSize: "12px",
  lineHeight: 1.4,
  textAlign: "center",
};

/**
 * LETTERHEAD_IMAGE_PREVIEW - Fixed-size preview surface for branding assets.
 *
 * RESPONSIBILITIES:
 * - Keep preview dimensions consistent for alignment.
 * - Preserve image aspect ratio without stretching.
 * - Show a stable unavailable-state placeholder when the source fails to load.
 *
 * @param {string} src - Preview image source URL or object URL.
 * @param {string} alt - Accessible alternate text for the preview image.
 * @param {string} dataCy - Optional Cypress selector for the preview frame.
 * @returns {JSX.Element} Fixed-size preview frame with image or placeholder content.
 */
export const LetterheadImagePreview = ({ src, alt, dataCy }) => {
  const [hasLoadError, setHasLoadError] = React.useState(false);

  React.useEffect(() => {
    setHasLoadError(false);
  }, [src]);

  return (
    <div
      data-cy={dataCy}
      data-testid="letterhead-image-preview"
      style={LETTERHEAD_PREVIEW_FRAME_STYLE}
    >
      {src && !hasLoadError ? (
        <img
          crossOrigin="anonymous"
          src={src}
          alt={alt}
          style={LETTERHEAD_PREVIEW_IMAGE_STYLE}
          onError={() => setHasLoadError(true)}
        />
      ) : src ? (
        <span style={LETTERHEAD_PREVIEW_PLACEHOLDER_STYLE}>Image unavailable</span>
      ) : null}
    </div>
  );
};

class LetterheadSetting extends Component {
  constructor(props) {
    super(props);
    this.state = {
      fields: {
        address: {
          street_address: {
            name: "street_address",
            label: "Company Address",
            type: "address",
            touched: false,
            error: false,
            valid: true,
            value: "",
            dataCy: "company-address-input",
            validation: [ValidatorTypes.REQUIRED],
          },
          suburb: {
            name: "suburb",
            label: "Company Suburb",
            type: "address",
            touched: false,
            error: false,
            valid: true,
            value: "",
            dataCy: "company-suburb-input",
            validation: [ValidatorTypes.REQUIRED],
          },
          state: {
            name: "state",
            label: "Company State",
            type: "address",
            touched: false,
            error: false,
            valid: true,
            value: "",
            dataCy: "company-state-input",
            validation: [ValidatorTypes.REQUIRED],
          },
          postcode: {
            name: "postcode",
            label: "Company Postcode",
            type: "text",
            touched: false,
            error: false,
            valid: true,
            value: "",
            dataCy: "company-postcode-input",
            validation: [ValidatorTypes.REQUIRED, ValidatorTypes.POSTCODE],
          },
          country: {
            name: "country",
            label: "Company Country",
            type: "address",
            touched: false,
            error: false,
            valid: true,
            value: "",
            dataCy: "company-country-input",
            validation: [ValidatorTypes.REQUIRED],
          }
        },
        letterhead_name: {
          name: "letterhead_name",
          label: "Company Name",
          type: "text",
          touched: false,
          error: false,
          valid: true,
          value: "",
          dataCy: "company-name-input",
          validation: [ValidatorTypes.REQUIRED],
        },
        letterhead_email: {
          name: "letterhead_email",
          label: "Company Email",
          type: "email",
          touched: false,
          error: false,
          valid: true,
          value: "",
          dataCy: "company-email-input",
          validation: [ValidatorTypes.REQUIRED, ValidatorTypes.EMAIL],
        },
        letterhead_phoneno: {
          name: "letterhead_phoneno",
          label: "Company Phone",
          type: "text",
          touched: false,
          error: false,
          valid: true,
          value: "",
          dataCy: "company-phone-input",
          validation: [ValidatorTypes.REQUIRED, ValidatorTypes.PHONE_NUMBER],
        },
        letterhead_website: {
          name: "letterhead_website",
          label: "Company Website",
          type: "text",
          touched: false,
          error: false,
          valid: true,
          value: "",
          dataCy: "company-website-input",
          validation: [ValidatorTypes.OPTIONAL, ValidatorTypes.WEBSITE],
        },
        overtime_1_rate: {
          name: "overtime_1_rate",
          label: "OverTime 1 Rates",
          type: "number",
          min: 0,
          touched: false,
          error: false,
          valid: true,
          value: "",
          dataCy: "overtime-1-rate-input",
          validation: [ValidatorTypes.OPTIONAL],
        },
        overtime_2_rate: {
          name: "overtime_2_rate",
          label: "OverTime 2 Rates",
          type: "number",
          min: 0,
          touched: false,
          error: false,
          valid: true,
          value: "",
          dataCy: "overtime-2-rate-input",
          validation: [ValidatorTypes.OPTIONAL],
        },
        finance_gst: {
          name: "finance_gst",
          label: GSTORVAT + " Rate",
          type: "number",
          min: 0,
          touched: false,
          error: false,
          valid: true,
          value: "",
          dataCy: "gst-rate-input",
          validation: [ValidatorTypes.REQUIRED, ValidatorTypes.MIN],
        },
        start_time: {
          name: "start_time",
          label: "Standard Work Start Time",
          type: "timepicker",
          format: "LT",
          touched: false,
          error: false,
          valid: true,
          value: null,
          dataCy: "start-time-picker",
          validation: [ValidatorTypes.REQUIRED],
        },
        finish_time: {
          name: "finish_time",
          label: "Standard Work Finish Time",
          type: "timepicker",
          format: "LT",
          touched: false,
          error: false,
          valid: true,
          value: null,
          dataCy: "finish-time-picker",
          validation: [ValidatorTypes.REQUIRED],
        },
        total_hrs: {
          name: "total_hrs",
          label: "Standard Work Hours per Day",
          type: "number",
          touched: false,
          error: false,
          valid: true,
          value: "",
          dataCy: "total-hrs-input",
          validation: [ValidatorTypes.REQUIRED, ValidatorTypes.SINGLE_DIGIT],
        },
        workingDays: {
          name: "workingDays",
          label: "Standard Working Days",
          days: {
            monday: {
              label: "Monday",
              value: false,
            },
            tuesday: {
              label: "Tuesday",
              value: false,
            },
            wednesday: {
              label: "Wednesday",
              value: false,
            },
            thursday: {
              label: "Thursday",
              value: false,
            },
            friday: {
              label: "Friday",
              value: false,
            },
            saturday: {
              label: "Saturday",
              value: false,
            },
            sunday: {
              label: "Sunday",
              value: false,
            },
          },
          value: [],
          touched: false,
          error: false,
          valid: true,
          dataCy: "working-days-input",
          validation: [ValidatorTypes.REQUIRED],
        },
        sunday_as_new_week: {
          name: "sunday_as_new_week",
          label: "Include Sunday in the new Week",
          hideLabel: true,
          type: "checkbox",
          value: false,
          touched: false,
          error: false,
          valid: true,
          dataCy: "sunday-as-new-week-input",
          validation: [],
        },
      },
      images: {
        logo: "",
        signature: "",
      },
      images_header: {
        logo_header: "",
        signature: "",
      },
      fileToUpload: {
        logo: [],
        signature: [],
      },
      letterHeadToUpload: {
        logo: [],
        signature: [],
      },
      selectFileType: "",
      deleteDialogState: false,
      deleteObject: null,
      allowedFileExtension: {
        images: [".png", ".jpg", ".jpeg"],
      },
    };
  }

  componentDidMount() {
    this.getLetterheadSetting();
  }

  setAddress = async (value) => {
    let parsedAddress = {};
    parsedAddress = JSON.parse(value || "{}");
    let address = this.state.fields.address
    for (let addressKey in parsedAddress) {
      address[addressKey] = { ...address[addressKey], value: parsedAddress[addressKey] }
    }
    return address
  }
  getLetterheadSetting = async (overrideFiles = false) => {
    let { fields, images, images_header } = this.state;
    const letterheadData = await this.props.getGlobalSetting("LETTERHEAD");
    if (idx(letterheadData, (_letterheadData) => _letterheadData.data))
      Object.entries(letterheadData.data).forEach(async ([key, value]) => {
        if (key === "letterhead_address" && !fields.address.street_address.value) {
          fields.address.street_address = { ...fields.address.street_address, value: value }
        } else if (key in fields) {
          const isAddress = (key === 'address')
          if (key === 'sunday_as_new_week') {
            const validValues = [true, 'true'];
            fields[key].value = validValues.includes(value) ? true : false || "";
          }
          else if (!isAddress) fields[key].value = value || "";
          else if (isAddress) {
            let parsedAddress = {};
            parsedAddress = JSON.parse(value || "{}");
            let address = this.state.fields.address
            for (let addressKey in parsedAddress) {
              address[addressKey] = { ...address[addressKey], value: parsedAddress[addressKey] }
            }
            fields[key] = address
          }
        } else if (key == 'logo') {
          images[key] = value || "";
        } else if (key == 'logo_header') {
          images_header[key] = value || ""
        }

        if (key === 'workingDays') {
          for (let day in fields.workingDays.days) {
            if (value.includes(day)) fields.workingDays.days[day].value = true;
          }
        }
        if (letterheadData.data.start_time) {
          fields.start_time.value = moment.utc(letterheadData.data.start_time, 'HH:mm').toDate();
        }
        if (letterheadData.data.finish_time) {
          fields.finish_time.value = moment.utc(letterheadData.data.finish_time, 'HH:mm').toDate();
        }
        if (!fields.start_time.value && !fields.finish_time.value) {
          fields.start_time.value = moment.utc().hours(7).minutes(0).seconds(0).milliseconds(0).toDate();
          fields.finish_time.value = moment.utc().hours(15).minutes(30).seconds(0).milliseconds(0).toDate();
        }
      });

    this.setState({
      fields,
      images,
      images_header,
      selectFileType: "",
      ...(overrideFiles ? { fileToUpload: { logo: [], signature: [], letterHeadToUpload: { logo: [], signature: [] } } } : {})
    });
  };

  validateAndPrepareData = async (fields) => {
    let isFormValid = true;

    for (const [key, value] of Object.entries(fields)) {
      if (key === 'address') {
        for (const addressKey in value) {
          const { error, valid } = ValidateField(value[addressKey], value[addressKey].value);
          fields.address[addressKey].touched = true;
          fields.address[addressKey].valid = valid;
          fields.address[addressKey].error = error;
          isFormValid &&= valid;
        }
      } else {
        const { error, valid } = ValidateField(value, value.value);
        fields[key].touched = true;
        fields[key].valid = valid;
        fields[key].error = error;
        isFormValid &&= valid;
      }
    }

    return isFormValid;
  }

  editLetterheadSetting = async () => {
    this.validateWorkingDays();
    const { fields, fileToUpload, letterHeadToUpload, images, images_header } = this.state;
    const isFormValid = await this.validateAndPrepareData.call(this, fields);

    const dataToSend = new FormData();

    delete dataToSend.workingDays;
    let workingDays = { days: {} };
    for (let key in fields.workingDays.days) {
      workingDays.days[key] = fields.workingDays.days[key].value;
    }
    dataToSend["workingDays"] = workingDays;
    if (!isFormValid) {
      if (!fields.workingDays.value.length) this.props.errorSnackBar(`Please select at least one ${fields.workingDays.label}`);
      this.setState({ fields });
      return;
    }

    const _fields = _.cloneDeep(fields);

    for await (const [key, value] of Object.entries(_fields)) {
      if (key === 'address') {
        for (const addressKey in value) {
          value[addressKey] = value[addressKey].value;
        }
        dataToSend.set(key, JSON.stringify(value));
      }
      else if (key === 'start_time' || key === 'finish_time') {
        const utcHour = moment.utc(value.value).format('HH:mm');
        dataToSend.append(key, utcHour);
      }
      else { dataToSend.append(key, value.value); }
    }

    fields && Object.entries(images).forEach(([key, value]) => {
      dataToSend.set(key, value);
    });

    fields && Object.entries(images_header).forEach(([key, value]) => {
      dataToSend.set(key, value);
    });

    Array.isArray(fileToUpload.logo) && fileToUpload.logo.forEach((file) => {
      dataToSend.append(`logo`, file);
    });

    Array.isArray(fileToUpload.signature) && fileToUpload.signature.forEach((file) => {
      dataToSend.append(`signature`, file);
    });

    Array.isArray(letterHeadToUpload.logo) && letterHeadToUpload.logo.forEach((file) => {
      dataToSend.append(`logo_header`, file);
    });

    Array.isArray(letterHeadToUpload.signature) && letterHeadToUpload.signature.forEach((file) => {
      dataToSend.append(`signature_header`, file);
    });
    await this.props.editGlobalSetting(dataToSend, "LETTERHEAD");

    this.getLetterheadSetting(true);
  };


  inputChangeHandler = (e, key = '') => {
    let fields = this.state.fields, isFormValid = true, value = "";
    const isStartAndFinishTime = (key === "start_time" || key === "finish_time");

    if (key === "sunday_as_new_week") value = e.target.checked;
    else value = isStartAndFinishTime ? e : e.target.value;

    fields[key || e.target.name].value = value;
    fields[key || e.target.name].touched = true;

    const { error, valid } = isStartAndFinishTime ? { error: false, valid: true } : ValidateField(fields[key || e.target.name], value);

    fields[key || e.target.name].error = error;
    fields[key || e.target.name].valid = valid;

    if (key === "start_time" || key === "finish_time") {
      const startVal = fields.start_time.value;
      const finishVal = fields.finish_time.value;
      if (startVal && finishVal) {
        let startMoment = moment(startVal);
        let finishMoment = moment(finishVal);
        if (finishMoment.isBefore(startMoment)) {
          finishMoment.add(1, "day");
        }
        const diff = moment.duration(finishMoment.diff(startMoment));
        const hoursDecimal = diff.asHours();
        fields.total_hrs.value = hoursDecimal.toFixed(2);
      }
    }
    Object.values(fields).every(field => isFormValid && !field.error);
    this.setState({ fields, isFormValid });
  };

  addressInputChangeHandler = (e) => {
    let newAddress = this.state.fields.address, isFormValid = true, value = "";
    const name = e.target.name;
    value = e.target.value;
    newAddress[name].value = value;
    newAddress[name].touched = true;
    const { error, valid } = ValidateField(newAddress[name], value);
    newAddress[name].error = error;
    newAddress[name].valid = valid;

    Object.values(newAddress).every(address => isFormValid && !address.error)
    this.setState({
      fields: { ...this.state.fields, address: newAddress }, isFormValid
    });
  }

  daysInputChangeHandler = (e, name = "") => {
    var value = e.target.checked || false;
    let fields = this.state.fields;
    fields.workingDays.days[name].value = value;
    fields.workingDays.touched = true;
    this.setState({ fields: fields });
  };

  validateWorkingDays() {
    let fields = this.state.fields;
    fields.workingDays.touched = true;
    fields.workingDays.value = [];
    for (let key in fields.workingDays.days) {
      if (fields.workingDays.days[key].value)
        fields.workingDays.value.push(key);
    }
    if (fields.workingDays.value.length === 0) {
      fields.workingDays.error = `Please select at least one ${fields.workingDays.label}`;
      fields.workingDays.valid = false;
    }
    let isFormValid = true;
    for (let key of Object.keys(fields)) {
      isFormValid = isFormValid && !fields[key].error;
    }

    this.setState({ fields: fields, isFormValid });
  }

  handleSelectFile = (selectFileType, id) => {
    this.setState({ selectFileType }, () => {
      document.getElementById(id).click();
    });
  };

  handleFileUpload = (e) => {
    let { selectFileType, fileToUpload, allowedFileExtension } = this.state
    let isValid = true;

    Array.from(e.target.files).map((file) => {
      const fileExt = file.name.substring(
        file.name.lastIndexOf(".") + 1,
        file.name.length
      );
      if (file.size > 1250000) {
        isValid = false;
        this.props.errorSnackBar("File size is too big. Maximum 1.25MB");
      }
      if (!allowedFileExtension.images.some((item) => item.includes(fileExt))) {
        isValid = false;
        this.props.errorSnackBar(`File format invalid! Allowed formats (${allowedFileExtension.images.join(",")})`
        );
      }
    });

    if (isValid)
      this.setState({
        fileToUpload: {
          ...fileToUpload,
          [selectFileType]: [...e.target.files],
        },
      });

    e.target.value = "";
  };

  handleHeaderUpload = (e) => {
    let { selectFileType, letterHeadToUpload, allowedFileExtension } = this.state
    let isValid = true;

    Array.from(e.target.files).map((file) => {
      const fileExt = file.name.substring(
        file.name.lastIndexOf(".") + 1,
        file.name.length
      );
      if (file.size > 1250000) {
        isValid = false;
        this.props.errorSnackBar("File size is too big. Maximum 1.25MB");
      }
      if (!allowedFileExtension.images.some((item) => item.includes(fileExt))) {
        isValid = false;
        this.props.errorSnackBar(`File format invalid! Allowed formats (${allowedFileExtension.images.join(",")})`
        );
      }
    });

    if (isValid)
      this.setState({
        letterHeadToUpload: {
          ...letterHeadToUpload,
          [selectFileType]: [...e.target.files],
        },
      });

    e.target.value = "";
  };

  deleteGlobalSetting = async (deleteObject) => {
    await this.props.deleteGlobalSetting(deleteObject.type, "LETTERHEAD");
    this.deleteDialogHandler();
    this.getLetterheadSetting();
  };

  deleteDialogHandler = (object = null) => {
    this.setState((prevState) => ({
      deleteDialogState: !prevState.deleteDialogState,
      deleteObject: object,
    }));
  };

  render() {
    const {
      fields,
      images,
      images_header,
      deleteDialogState,
      deleteObject,
      allowedFileExtension,
      fileToUpload,
      letterHeadToUpload
    } = this.state;
    const writePermission = this.props.writePermission;
    let workingDaysFormElements = Object.keys(fields.workingDays.days).map((key) => {
      return (
        <Form key={key} className="usercheckbox me-3">
          <div className="table-checkbox">
            <Form.Check
              type={'checkbox'}
              id={`${key}-check`}
              label={fields.workingDays.days[key].label}
              value={fields.workingDays.days[key].value}
              checked={fields.workingDays.days[key].value}
              data-cy={`available-days-form-element-checkbox-${key}`}
              onChange={(e) => this.daysInputChangeHandler(e, key)}
            />
          </div>
        </Form>
      );
    });

    return (
      <>
        <DeleteDialog
          open={deleteDialogState}
          id={deleteObject}
          deleteHandler={this.deleteGlobalSetting}
          dialogClose={this.deleteDialogHandler}
          moduleName={"Selected File"}
          description={"Selected File"}
          dataCy="delete-dialog-button"
        />
        <div className="purchase-order-block" data-cy="letterhead-setting-container">
          <div className="row">
            <div className="col-12">
              <h2 data-cy="company-details-heading" className="content-heading content-heading-lg text-uppercase mt-0 me-auto">Company Details</h2>
            </div>

            {/* <div className="col-lg-8"> */}
            {/* <div className="row"> */}
            <div className="col-lg-4 col-md-6 mt-3">
              <CustomInput
                {...fields.letterhead_name}
                disabled={!writePermission}
                onChange={this.inputChangeHandler}
              />
            </div>
            <div className="col-lg-4 col-md-6 mt-3">
              <CustomInput
                {...fields.letterhead_website}
                disabled={!writePermission}
                onChange={this.inputChangeHandler}
              />
            </div>
            <div className="col-lg-4 col-md-6 mt-3">
              <CustomInput
                {...fields.address.street_address}
                disabled={!writePermission}
                onChange={this.addressInputChangeHandler}
              />
            </div>
            <div className="col-lg-4 col-md-6 mt-3">
              <CustomInput
                {...fields.address.suburb}
                disabled={!writePermission}
                onChange={this.addressInputChangeHandler}
              />
            </div>
            <div className="col-lg-4 col-md-6 mt-3">
              <CustomInput
                {...fields.address.state}
                disabled={!writePermission}
                onChange={this.addressInputChangeHandler}
              />
            </div>
            <div className="col-lg-4 col-md-6 mt-3">
              <CustomInput
                {...fields.address.postcode}
                disabled={!writePermission}
                onChange={this.addressInputChangeHandler}
              />
            </div>
            <div className="col-lg-4 col-md-6 mt-3">
              <CustomInput
                {...fields.address.country}
                disabled={!writePermission}
                onChange={this.addressInputChangeHandler}
              />
            </div>
            <div className="col-lg-4 col-md-6 mt-3">
              <CustomInput
                {...fields.letterhead_phoneno}
                disabled={!writePermission}
                onChange={this.inputChangeHandler}
              />
            </div>
            <div className="col-lg-4 col-md-6 mt-3">
              <CustomInput
                {...fields.letterhead_email}
                disabled={!writePermission}
                onChange={this.inputChangeHandler}
              />
            </div>
            <div className="col-lg-4 col-md-6 mt-3">
              <NumberInput
                {...fields.finance_gst}
                disabled={!writePermission}
                onChange={(e) => this.inputChangeHandler(e, fields.finance_gst.name)}
                onBlur={(e) => this.inputChangeHandler(e, fields.finance_gst.name)}
              />
            </div>
            <div className="col-lg-4 col-md-6 mt-3">
              <TimePickerInput
                {...fields.start_time}
                value={fields.start_time.value}
                disabled={!writePermission}
                onChange={(e) => this.inputChangeHandler(e, fields.start_time.name)}
              />
            </div>
            <div className="col-lg-4 col-md-6 mt-3">
              <TimePickerInput
                {...fields.finish_time}
                value={fields.finish_time.value}
                disabled={!writePermission}
                onChange={(e) => this.inputChangeHandler(e, fields.finish_time.name)}
              />
            </div>
            <div className="col-lg-4 col-md-6 mt-3">
              <CustomInput
                {...fields.total_hrs}
                disabled={!writePermission}
                onChange={(e) => this.inputChangeHandler(e, fields.total_hrs.name)}
              />
            </div>
          </div>
          <div className="row" data-cy="working-days-row">
            <div className="col-12 mt-3">
              <h2 data-cy="working-days-heading" className="content-heading content-heading-lg text-uppercase my-auto me-auto">
                Working Days
              </h2>
            </div>
            <div className="col-12 mt-3 text-left" data-cy="working-days-container">
              {workingDaysFormElements}
            </div>
            <div className="col-lg-4 col-md-6 mt-3 text-left">
              <Form className="usercheckbox">
                <div className="table-checkbox">
                  <Form.Check
                    type={'checkbox'}
                    id={`sunday-as-new-week-check`}
                    label={fields.sunday_as_new_week.label}
                    value={fields.sunday_as_new_week.value}
                    checked={fields.sunday_as_new_week.value}
                    data-cy={fields.sunday_as_new_week.dataCy}
                    onChange={(e) => this.inputChangeHandler(e, fields.sunday_as_new_week.name)}
                  />
                </div>
              </Form>
            </div>
          </div>
          <div className="row mt-4">
            <div className="col-6">
              <div className="row">
                <div className="col-12 d-flex gap-5">
                  <h2 className="content-heading content-heading-lg text-uppercase my-auto">Logo</h2>
                  {writePermission && (<Button onClick={() => this.handleSelectFile("logo", "fileInput")} className="secondarybtn">
                    Update
                  </Button>)}
                </div>
                <div className="col-lg-4 col-md-4 mt-3">
                  <LetterheadImagePreview
                    src={
                      fileToUpload.logo.length > 0
                        ? window.URL.createObjectURL(new Blob([fileToUpload.logo[0]]))
                        : images.logo
                    }
                    alt="logo"
                    dataCy="logo-preview"
                  />
                </div>
                <div className="col-lg-4 col-md-4 col-6 align-self-center text-left">
                  {images.logo && writePermission && (fileToUpload.logo || []).length < 1 && (
                    <div className="w-auto ms-auto">
                      <span
                        onClick={() =>
                          this.deleteDialogHandler({
                            url: images.logo,
                            type: "logo",
                          })
                        }
                        className="filter-icon text-danger">
                        <DeleteIcon />
                      </span>
                    </div>
                  )}
                </div>
                <div className="col-lg-4 col-md-4 col-6 mt-3">
                  <input
                    style={{ marginBottom: "15px", display: "none" }}
                    id="fileInput"
                    name="attachments"
                    type="file"
                    onChange={this.handleFileUpload}
                    accept={allowedFileExtension.images.join(",")}
                  />
                </div>
              </div>
            </div>
            <div className="col-6">
              <div className="row">
                <div className="col-12 d-flex gap-5">
                  <h2 className="content-heading content-heading-lg text-uppercase my-auto">Letterhead</h2>
                  {writePermission && (<Button onClick={() => this.handleSelectFile("logo", "fileInputHeader")} className="secondarybtn">
                    Update
                  </Button>)}
                </div>
                <div className="col-lg-4 col-md-4 mt-3">
                  <LetterheadImagePreview
                    src={
                      letterHeadToUpload.logo.length > 0
                        ? window.URL.createObjectURL(new Blob([letterHeadToUpload.logo[0]]))
                        : images_header.logo_header
                    }
                    alt="letterhead"
                    dataCy="letterhead-preview"
                  />
                </div>
                <div className="col-lg-4 col-md-4 col-6 align-self-center text-left">
                  {images_header.logo_header && writePermission && (letterHeadToUpload.logo || []).length < 1 && (
                    <div className="w-auto ms-auto">
                      <span
                        onClick={() =>
                          this.deleteDialogHandler({
                            url: images_header.logo_header,
                            type: "logo_header",
                          })
                        }
                        className="filter-icon text-danger">
                        <DeleteIcon />
                      </span>
                    </div>
                  )}
                </div>
                <div className="col-lg-4 col-md-4 col-6 mt-3">
                  <input
                    style={{ marginBottom: "15px", display: "none" }}
                    id="fileInputHeader"
                    name="attachments"
                    type="file"
                    onChange={this.handleHeaderUpload}
                    accept={allowedFileExtension.images.join(",")}
                  />
                </div>
              </div>
            </div>
          </div>

          {writePermission && (<div className="col-12 d-flex mt-3">
            <Button data-cy="save-button" onClick={this.editLetterheadSetting} className="ms-auto me-3 secondarybtn">SAVE</Button>
          </div>)}
        </div>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  globalSetting: state.globalSetting
});

const mapDispatchToProps = (dispatch) => ({
  getGlobalSetting: bindActionCreators(getGlobalSetting, dispatch),
  editGlobalSetting: bindActionCreators(editGlobalSetting, dispatch),
  errorSnackBar: bindActionCreators(errorSnackBar, dispatch),
  deleteGlobalSetting: bindActionCreators(deleteGlobalSetting, dispatch),
  getGlobalSettingReducer: bindActionCreators(getGlobalSettingReducer, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(LetterheadSetting);
